import { useEffect, useState } from 'react';
import { getContextMenu } from '../../service/areena-api/fetchers';
import { Control, Language, Pointer } from '@yleisradio/areena-types';
import { logger } from '../../common/logger';
import { getTranslation } from '../localizations/translations';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';

type Grouping = {
  controls: Control[];
  groupings: Grouping[];
  type: 'grouping';
};

export type ContextMenuResponse = {
  data: ContextMenuData;
  apiVersion: string;
  meta: unknown;
};

export type ContextMenuData = {
  groupings: Grouping[];
  type: 'menu';
};

const closeControl = (title: string): Control => ({
  type: 'close',
  title,
  icon: { id: 'cancel', version: 'x' },
});

const getCloseControl = (languageCode: Language) =>
  closeControl(getTranslation('common_close', languageCode));

const mapControls = (
  contextMenu: ContextMenuResponse,
  languageCode: Language
): Control[] => {
  if (contextMenu.data.type !== 'menu') {
    logger.error({
      error: `Invalid context menu type: ${contextMenu.data.type}`,
    });
    return [];
  }

  const controls: Control[] = [];
  contextMenu.data.groupings.forEach(grouping => {
    grouping.controls.forEach(control => {
      controls.push(control);
    });
  });
  controls.push(getCloseControl(languageCode));

  return controls;
};

export function useContextMenu(pointer?: Pointer) {
  const { languageCode } = useAppFeaturesContext();
  const [data, setData] = useState<Control[]>([]);

  const getMenuControls = async (pointer: Pointer) => {
    const menu = await getContextMenu(pointer);
    return mapControls(menu, languageCode);
  };

  useEffect(() => {
    if (!pointer || !pointer.uri) {
      return;
    }

    getMenuControls(pointer)
      .then(controls => setData(controls))
      .catch(error => {
        logger.error({ error }, 'Error fetching context menu');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageCode, pointer]);

  return { data };
}
